import { useEffect, useState } from "react";
import { dashboardService } from "../../services/api";
import { fmtDateTimezone } from "../utils/dateUtils/fmtDate";
import StatCard from "../../components/StatCard/StatCard";
import StatusPill from "../../components/StatusPill/StatusPill";
import { useNotification } from "../../components/Notifications/NotificationContext";
import { CheckCircle2, Hourglass, Search, XCircle } from "lucide-react";
import useUrlFilters from "./useUrlFilters";
import AdminFilters from "./AdminFilters";
import { AdminMetrics } from "./AdminMetrics";

const STATUS_COLORS = {
    pending: "yellow",
    reviewing: "blue",
    resolved: "green",
    rejected: "red",
};

const STATUS_LABELS = {
    pending: "Pendente",
    reviewing: "Em análise",
    resolved: "Resolvida",
    rejected: "Rejeitada",
};

const FILTER_KEYS = ["status", "bot_username"];
const SEARCHES = [{ key: "bot_username", placeholder: "@ do bot denunciado" }];
const SELECTS = [{
    key: "status", label: "Status", options: [
        { value: "", label: "Todos os status" },
        ...Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label })),
    ],
}];

export default function AdminReports() {
    const [reports, setReports] = useState([]);
    const [statusCounts, setStatusCounts] = useState({});
    const [loading, setLoading] = useState(false);
    const { showError, showSuccess } = useNotification();
    const [filters, applyFilters, filtersKey] = useUrlFilters(FILTER_KEYS);
    const [pagination, setPagination] = useState({});
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        loadReports(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersKey]);

    const loadReports = async (page = 1) => {
        try {
            setLoading(true);
            const { data } = await dashboardService.getAdminReports({ page, ...filters });
            setReports(data.reports || []);
            setStatusCounts(data.status_counts || {});
            setPagination(data.pagination || {});
            setCurrentPage(page);
        } catch (err) {
            console.error(err);
            showError("Erro ao carregar denúncias.");
        } finally {
            setLoading(false);
        }
    };

    const handleStatus = async (report, newStatus) => {
        try {
            await dashboardService.updateReportStatus(report.id, { status: newStatus });
            showSuccess(`Denúncia #${report.id}: ${STATUS_LABELS[newStatus]}.`);
            loadReports(currentPage);
        } catch (err) {
            console.error(err);
            showError("Erro ao atualizar. " + (err?.response?.data?.error || ""));
        }
    };

    return (
        <>
            <AdminFilters values={filters} onApply={applyFilters} searches={SEARCHES} selects={SELECTS} />

            <AdminMetrics>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard title="Pendentes" value={statusCounts.pending || 0} icon={{ Icon: Hourglass, color: "red" }} />
                    <StatCard title="Em análise" value={statusCounts.reviewing || 0} icon={{ Icon: Search, color: "blue" }} />
                    <StatCard title="Resolvidas" value={statusCounts.resolved || 0} icon={{ Icon: CheckCircle2, color: "green" }} />
                    <StatCard title="Rejeitadas" value={statusCounts.rejected || 0} icon={{ Icon: XCircle, color: "purple" }} />
                </div>
            </AdminMetrics>

            {/* CARDS DE DENÚNCIAS */}
            <div className="admin-cards">
                {reports.map((r) => (
                    <div key={r.id} className="admin-card">
                        <div className="admin-card-head">
                            <div className="admin-avatar"><i className="fas fa-flag" /></div>
                            <div className="admin-card-id">
                                <div className="admin-card-name-row">
                                    <span className="admin-card-name">@{r.reported_bot_username}</span>
                                    <span className="admin-chip admin-chip--danger">
                                        {r.report_type_label}
                                    </span>
                                </div>
                                <span className="admin-card-sub">
                                    <i className="fas fa-user" /> Denunciado por: {r.reporter_username} ({r.reporter_email})
                                </span>
                                <div className="admin-bot-dates">
                                    <span><i className="fas fa-calendar" /> {fmtDateTimezone(r.created_at)}</span>
                                    {r.platform && <span><i className="fas fa-globe" /> {r.platform_label}</span>}
                                    {r.reviewed_at && <span><i className="fas fa-gavel" /> Analisada: {fmtDateTimezone(r.reviewed_at)}</span>}
                                </div>
                            </div>
                            <StatusPill color={STATUS_COLORS[r.status] || "gray"}>
                                {r.status_label}
                            </StatusPill>
                        </div>

                        <div className="admin-copy-preview">{r.description}</div>

                        {(r.redirect_link || r.content_link || r.pix_code) && (
                            <div className="admin-bot-dates" style={{ flexDirection: "column", gap: 6 }}>
                                {r.redirect_link && (
                                    <span style={{ overflowWrap: "anywhere" }}>
                                        <i className="fas fa-random" /> Redirecionador: {r.redirect_link}
                                    </span>
                                )}
                                {r.content_link && (
                                    <span style={{ overflowWrap: "anywhere" }}>
                                        <i className="fas fa-link" /> Conteúdo: {r.content_link}
                                    </span>
                                )}
                                {r.pix_code && (
                                    <span style={{ overflowWrap: "anywhere" }}>
                                        <i className="fas fa-qrcode" /> PIX: {r.pix_code}
                                    </span>
                                )}
                            </div>
                        )}

                        <div className="admin-card-footer">
                            {r.status === "pending" && (
                                <button type="button" className="admin-icon-btn is-blue" onClick={() => handleStatus(r, "reviewing")}>
                                    <i className="fas fa-magnifying-glass" /><span>Analisar</span>
                                </button>
                            )}
                            {r.status !== "resolved" && (
                                <button type="button" className="admin-icon-btn is-success" onClick={() => handleStatus(r, "resolved")}>
                                    <i className="fas fa-check" /><span>Resolver</span>
                                </button>
                            )}
                            {r.status !== "rejected" && (
                                <button type="button" className="admin-icon-btn is-danger" onClick={() => handleStatus(r, "rejected")}>
                                    <i className="fas fa-times" /><span>Rejeitar</span>
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {reports.length === 0 && !loading && (
                <div className="empty-state">
                    <i className="fas fa-flag" />
                    <h4>Nenhuma denúncia encontrada</h4>
                </div>
            )}

            {/* PAGINAÇÃO */}
            {!loading && reports.length > 0 && (
                <div className="mt-8 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                        Página {currentPage} de {pagination.pages} ({pagination.total} denúncias)
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => loadReports(currentPage - 1)}
                            disabled={!pagination.has_prev}
                            className="btn-secondary"
                        >
                            <i className="fas fa-chevron-left"></i> Anterior
                        </button>
                        <button
                            onClick={() => loadReports(currentPage + 1)}
                            disabled={!pagination.has_next}
                            className="btn-primary"
                        >
                            Próximo <i className="fas fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
